import React, { useState, useRef, useEffect } from 'react';
import type { ArtifactStatus } from '../../api/services/artifacts';

export interface StatusSelectorProps {
  currentStatus: ArtifactStatus;
  onStatusChange: (status: ArtifactStatus) => void | Promise<void>;
  className?: string;
}

const statusOptions: Array<{ value: ArtifactStatus; label: string; color: string }> = [
  { value: 'draft', label: 'Draft', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'ready', label: 'Ready', color: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'sent', label: 'Sent', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  {
    value: 'archived',
    label: 'Archived',
    color: 'bg-neutral-50 text-neutral-600 border-neutral-200',
  },
];

export const StatusSelector: React.FC<StatusSelectorProps> = ({
  currentStatus,
  onStatusChange,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentOption =
    statusOptions.find((opt) => opt.value === currentStatus) || statusOptions[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleStatusSelect = async (status: ArtifactStatus) => {
    if (status !== currentStatus) {
      await onStatusChange(status);
    }
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className={`relative inline-flex items-center ${className}`}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition hover:shadow-sm h-[22px] ${currentOption.color}`}
      >
        {currentOption.label}
        <svg className="ml-1.5 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 w-40 origin-top-right rounded-lg border border-neutral-200 bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleStatusSelect(option.value);
                }}
                className={`w-full text-left px-3 py-2 text-sm transition ${
                  option.value === currentStatus
                    ? 'bg-neutral-50 font-semibold'
                    : 'hover:bg-neutral-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex h-2 w-2 rounded-full ${
                      option.value === 'draft'
                        ? 'bg-amber-500'
                        : option.value === 'ready'
                          ? 'bg-green-500'
                          : option.value === 'sent'
                            ? 'bg-blue-500'
                            : 'bg-neutral-400'
                    }`}
                  />
                  <span>{option.label}</span>
                  {option.value === currentStatus && (
                    <svg
                      className="ml-auto h-4 w-4 text-neutral-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusSelector;
